import dayjs from 'dayjs';
import * as Yup from 'yup';

import {
  APPEARANCE_BACKGROUND_TABS,
  DATE_FORMAT,
  DEFAULT_LANGUAGE,
  EXIT_INTENT,
  FIELD_TYPES,
  FONT_WEIGHTS,
  GRADIENT_DIRECTION_MAP,
  GRADIENT_DIRECTIONS,
  LANGUAGES,
} from './constants';
import { getTranslation } from './translations';

export const getEmbedWebsiteUrl = () => {
  if (typeof window === 'undefined') return '';
  const locationParam = new URLSearchParams(window.location.search).get('location');
  if (locationParam) return locationParam;
  return typeof document !== 'undefined' && document.referrer ? document.referrer : window.location.href;
};

export const mapTextStyles = settings => ({
  bold: settings?.isBold ?? false,
  italic: settings?.isItalic ?? false,
  underline: settings?.isUnderlined ?? false,
});

export const getTextStyle = (size, styles = {}, alignment = 'center', color) => {
  const normalizedStyles = mapTextStyles(styles);

  return {
    fontSize: size ? `${size}px` : '',
    fontWeight: normalizedStyles?.bold ? FONT_WEIGHTS.BOLD : FONT_WEIGHTS.REGULAR,
    fontStyle: normalizedStyles?.italic ? 'italic' : undefined,
    textDecoration: normalizedStyles?.underline ? 'underline' : undefined,
    textAlign: alignment,
    color,
  };
};

export const toSnakeCase = obj => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      let snakeKey = key.toLowerCase().replace(/\s+/g, '_');
      return [snakeKey, value];
    })
  );
};

export const getFileNameFromUrl = url => {
  if (typeof url !== 'string') return '';

  const fileName = url.split('/').pop()?.split('?')[0] || '';
  const dotIndex = fileName.lastIndexOf('.');
  const underscoreIndex = fileName.lastIndexOf('_');

  if (dotIndex === -1 || underscoreIndex === -1) return fileName;

  const name = fileName.slice(0, underscoreIndex);
  const extension = fileName.slice(dotIndex);

  return `${name}${extension}`;
};

export const replaceWhiteSpaceWithUnderscore = str => {
  return str.replace(/\s+/g, '_');
};

/**
 * Extracts a language code from a label like "Swedish (SV)".
 *
 * @param {string} label - The language label from the API.
 * @returns {string|null} The normalized language code or null if not found.
 */
const getLanguageCodeFromLabel = label => {
  if (typeof label !== 'string') return null;

  const codeMatch = label.match(/\(([^)]+)\)/);
  if (codeMatch?.[1]) return codeMatch[1].trim().toLowerCase();

  const normalized = label.toLowerCase();
  if (normalized.includes('english')) return 'en';
  if (normalized.includes('finnish') || normalized.includes('suomi')) return 'fi';
  if (normalized.includes('swedish') || normalized.includes('svenska')) return 'sv';
  if (normalized.includes('german') || normalized.includes('deutsch')) return 'de';

  return null;
};

/**
 * Returns the list of enabled language codes based on the languagesEnabled array.
 * Falls back to the default LANGUAGES order.
 *
 * @param {Array} languagesEnabled - Array of language objects with label/value.
 * @returns {string[]} Array of enabled language codes (e.g., ['en', 'fi'])
 */
export const getEnabledLanguageCodes = languagesEnabled => {
  if (!Array.isArray(languagesEnabled) || languagesEnabled.length === 0) {
    return LANGUAGES.map(lang => lang.code);
  }

  const supportedCodes = new Set(LANGUAGES.map(({ code }) => code));

  const enabledCodes = languagesEnabled
    .map((lang, index) => ({
      code: getLanguageCodeFromLabel(lang.label),
      value: lang.value,
      order: typeof lang.order === 'number' ? lang.order : Number.MAX_SAFE_INTEGER,
      index,
    }))
    .filter(({ code, value }) => supportedCodes.has(code) && value === true)
    .sort((a, b) => {
      if (a.order === b.order) return a.index - b.index;
      return a.order - b.order;
    })
    .map(({ code }) => code);

  const uniqueEnabledCodes = [...new Set(enabledCodes)];

  return uniqueEnabledCodes.length ? uniqueEnabledCodes : LANGUAGES.map(lang => lang.code);
};

/**
 * Language order used to resolve a per-language label dict to a single stable string.
 * Matches the backend `settings.LANGUAGES` order so the submitted response key never
 * depends on which language the field was authored in.
 */
export const PRIMARY_LABEL_LANGUAGE_ORDER = ['fi', 'en', 'de', 'sv'];

/**
 * Resolves a lead-field label to a single plain string.
 * Mirrors the backend `primary_label_text`: returns the first non-empty value in
 * PRIMARY_LABEL_LANGUAGE_ORDER. Accepts a legacy plain string for backward safety.
 *
 * @param {Object|string} label - Per-language label dict or a legacy plain string.
 * @returns {string} The primary-language label text, or an empty string.
 */
export const primaryLabelText = label => {
  if (label && typeof label === 'object') {
    const code = PRIMARY_LABEL_LANGUAGE_ORDER.find(langCode => label[langCode]);
    return code ? label[code] : '';
  }
  return typeof label === 'string' ? label : '';
};

/**
 * Returns the initial values for the fields based on the provided fields.
 *
 * @param {Array} fields - The fields array.
 * @returns {Object} The initial values object.
 */

export const getInitialValues = fields => {
  const values = {};
  fields?.forEach(({ label: rawLabel, type, isMultiSelect }) => {
    const label = primaryLabelText(rawLabel);
    if (!label) return;
    const fieldType = type?.toLowerCase?.() || type;
    if (fieldType === FIELD_TYPES.DROPDOWN && isMultiSelect) {
      values[label] = [];
    } else if (fieldType === FIELD_TYPES.DROPDOWN) {
      values[label] = '';
    } else if (fieldType === FIELD_TYPES.UPLOAD) {
      values[label] = '';
    } else if (fieldType === FIELD_TYPES.CONSENT) {
      values[label] = false;
    } else {
      values[label] = '';
    }
  });
  return values;
};

/**
 * Returns the localized value for a given custom and default value based on the provided active language.
 *
 * It attempts to find the value in the following order:
 *   1. customValue for the activeLang
 *   2. customValue in English ('en')
 *   3. defaultValue for the activeLang
 *   4. defaultValue in English ('en')
 *
 * @param {Object} customValue - The custom object containing key/value pairs of language codes and values.
 * @param {Object} defaultValue - The default object containing key/value pairs of language codes and values.
 * @param {string} activeLang - The active language code to use for localization.
 * @returns {string|undefined} The localized value based on the priority order, or undefined if not found.
 */
export const getLocalizedValue = (customValue, defaultValue, activeLang) =>
  customValue?.[activeLang] ||
  customValue?.[DEFAULT_LANGUAGE] ||
  defaultValue?.[activeLang] ||
  defaultValue?.[DEFAULT_LANGUAGE];

export const generateValidationSchema = (fields, activeLang) => {
  const schema = {};
  const requiredMsg = getTranslation('validation.required', activeLang);
  const invalidEmailMsg = getTranslation('validation.invalidEmail', activeLang);
  const numericOnlyMsg = getTranslation('validation.numericOnly', activeLang);

  fields?.forEach(({ label: rawLabel, type, isRequired, isMultiSelect }) => {
    const label = primaryLabelText(rawLabel);
    if (!label) return;
    const fieldType = type?.toLowerCase?.() || type;
    let validator;

    if (fieldType === FIELD_TYPES.CONSENT) {
      validator = Yup.boolean();
    } else if (fieldType === FIELD_TYPES.DROPDOWN && isMultiSelect) {
      validator = Yup.array()
        .of(Yup.string())
        .test('nonEmptyArray', requiredMsg, value => !isRequired || (Array.isArray(value) && value.length > 0));
    } else if (fieldType === FIELD_TYPES.DROPDOWN) {
      validator = Yup.string().nullable();
      if (isRequired) {
        validator = validator.test(
          'required',
          requiredMsg,
          value => value !== null && value !== undefined && value !== ''
        );
      }
    } else {
      switch (fieldType) {
      case FIELD_TYPES.EMAIL:
        validator = Yup.string().email(invalidEmailMsg);
        break;
      case FIELD_TYPES.NUMBER:
        validator = Yup.string().matches(/^\d+$/, numericOnlyMsg);
        break;
      case FIELD_TYPES.UPLOAD:
        validator = Yup.mixed().test(
          'fileRequired',
          requiredMsg,
          value => !isRequired || (value && value.length > 0)
        );
        break;
      case FIELD_TYPES.DATE:
        validator = Yup.mixed().test('validDate', requiredMsg, value => {
          const isDate = val => val instanceof Date && !isNaN(val.getTime());

          if (!value) return !isRequired;

          return isDate(value);
        });
        break;
      case FIELD_TYPES.TEXTAREA:
      default:
        validator = Yup.string();
      }

      if (isRequired && fieldType !== FIELD_TYPES.UPLOAD) {
        validator = validator.required(requiredMsg);
      }
    }

    schema[label] = validator;
  });
  return Yup.object().shape(schema);
};

/**
 * Sorts the fields by the sorting order.
 *
 * @param {Array} fields - The fields array.
 * @returns {Array} The sorted fields array.
 */

export const sortFieldsByOrder = fields =>
  [...(fields || [])].sort((a, b) => (a.sortingOrder ?? 0) - (b.sortingOrder ?? 0));

/**
 * Formats the date value based on the provided value.
 *
 * @param {string} value - The value to format.
 * @returns {string} The formatted date value.
 */

const formatDateValue = value => {
  const isValidDate = val => val instanceof Date && !isNaN(val.getTime());

  if (isValidDate(value)) {
    return dayjs(value).format(DATE_FORMAT.STANDARD_DATE);
  }
  return value;
};

/**
 * Formats the values for the payload based on the provided values.
 *
 * @param {Object} values - The values object.
 * @returns {Object} The formatted values object.
 */

export const formatValuesForPayload = values =>
  Object.entries(values || {}).reduce((acc, [key, value]) => {
    acc[key] = formatDateValue(value);
    return acc;
  }, {});

/**
 * Formats the current time in the format of day-month-year:hour:minute:period.
 *
 * @returns {string} The formatted current time.
 */

export const formatTimeStamp = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

export const LAYOUT_MODE = {
  IMAGE_LEFT: 'IMAGE_LEFT',
  IMAGE_RIGHT: 'IMAGE_RIGHT',
  IMAGE_BACKGROUND: 'IMAGE_BACKGROUND',
};

const LAYOUT_FLEX_DIRECTION = {
  [LAYOUT_MODE.IMAGE_LEFT]: 'row',
  [LAYOUT_MODE.IMAGE_RIGHT]: 'row-reverse',
};

export const layoutToFlexDirection = layoutMode =>
  LAYOUT_FLEX_DIRECTION[layoutMode] ?? LAYOUT_FLEX_DIRECTION[LAYOUT_MODE.IMAGE_LEFT];

export const isHorizontalLayout = layoutMode =>
  layoutMode === LAYOUT_MODE.IMAGE_LEFT || layoutMode === LAYOUT_MODE.IMAGE_RIGHT;

export const isImageBackgroundLayout = layoutMode => layoutMode === LAYOUT_MODE.IMAGE_BACKGROUND;

/**
 * Returns the background style object based on the provided formik values.
 *
 * When backgroundType is IMAGE and layoutMode is IMAGE_BACKGROUND, the image
 * is rendered as the container background (legacy behavior). For other layout
 * modes the image is rendered as a separate flex child by the consumer.
 *
 * @param {Object} formikValues - The formik values object.
 * @returns {Object} The background style object.
 */

export const getBackgroundStyle = formikValues => {
  const {
    backgroundType,
    backgroundColor,
    gradientDirection,
    gradientPrimaryColor,
    gradientSecondaryColor,
    backgroundImage,
    layoutMode,
  } = formikValues;

  const styles = {};
  const direction =
    gradientDirection === GRADIENT_DIRECTIONS[1].label
      ? GRADIENT_DIRECTION_MAP.HORIZONTAL
      : GRADIENT_DIRECTION_MAP.VERTICAL;

  if (backgroundType === APPEARANCE_BACKGROUND_TABS.SOLID && backgroundColor) {
    styles.backgroundColor = backgroundColor;
  }

  if (backgroundType === APPEARANCE_BACKGROUND_TABS.GRADIENT && gradientPrimaryColor && gradientSecondaryColor) {
    styles.backgroundImage = `linear-gradient(${direction}, ${gradientPrimaryColor}, ${gradientSecondaryColor})`;
  }

  if (backgroundType === APPEARANCE_BACKGROUND_TABS.IMAGE) {
    if (isImageBackgroundLayout(layoutMode) && backgroundImage?.image) {
      styles.backgroundImage = `url(${backgroundImage.image})`;
      styles.backgroundPosition = 'center';
      styles.backgroundRepeat = 'no-repeat';
      styles.backgroundSize = 'cover';
    } else if (backgroundColor) {
      styles.backgroundColor = backgroundColor;
    }
  }

  return styles;
};

export const hostSupportsExitIntent = () =>
  new URLSearchParams(window.location.search).get(EXIT_INTENT.SUPPORT_PARAM) === EXIT_INTENT.SUPPORT_VERSION;

export const hasFinePointer = () => window.matchMedia?.('(pointer: fine)')?.matches === true;
